/// <reference types="google.maps" />
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { MapPin, Trash2, UserX, CheckCircle2, XCircle, Pencil, ExternalLink, ShieldAlert, Loader2, AlertTriangle } from 'lucide-react';
import {
  useAdminPlaceDisputes,
  useResolvePlaceDispute,
  useAdminUpdatePlace,
  AdminPlaceDispute,
  DisputeAction,
} from '@/hooks/useAdminPlaceDisputes';
import { useGoogleMapsLoader } from '@/hooks/useGoogleMapsLoader';

const PLACE_CATEGORIES = ['dog_park', 'cat_cafe', 'dog_cafe', 'pet_shop', 'restaurant', 'veterinary'];

const statusBadge = (s: string) => {
  const variant: any = s === 'open' || s === 'pending' ? 'destructive' : s === 'resolved' ? 'default' : 'secondary';
  return <Badge variant={variant}>{s}</Badge>;
};

function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

type GeocodeResult =
  | { status: 'match'; formatted: string; distanceM: number }
  | { status: 'mismatch'; formatted: string; distanceM: number }
  | { status: 'notfound' }
  | { status: 'error'; message: string };

const AdminPlacesPanel: React.FC = () => {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const { data: disputes, isLoading } = useAdminPlaceDisputes(statusFilter === 'all' ? null : statusFilter);
  const resolve = useResolvePlaceDispute();
  const update = useAdminUpdatePlace();
  const { isLoaded: mapsLoaded } = useGoogleMapsLoader();

  const [editing, setEditing] = useState<AdminPlaceDispute | null>(null);
  const [form, setForm] = useState({
    name: '',
    categories: [] as string[],
    address: '',
    pet_policy_summary: '',
    is_verified: false,
  });
  const [verifying, setVerifying] = useState(false);
  const [geocode, setGeocode] = useState<GeocodeResult | null>(null);

  const openEdit = (d: AdminPlaceDispute) => {
    setEditing(d);
    const cats =
      d.place_categories && d.place_categories.length > 0
        ? d.place_categories
        : d.place_category
          ? [d.place_category]
          : [];
    setForm({
      name: d.place_name ?? '',
      categories: cats,
      address: d.place_address ?? '',
      pet_policy_summary: '',
      is_verified: false,
    });
    setGeocode(null);
  };

  const toggleCategory = (c: string) => {
    setForm((f) =>
      f.categories.includes(c)
        ? { ...f, categories: f.categories.filter((x) => x !== c) }
        : { ...f, categories: [...f.categories, c] },
    );
  };

  const handleVerifyAddress = async () => {
    if (!editing || !form.address.trim()) return;
    if (!mapsLoaded || typeof google === 'undefined' || !google.maps?.Geocoder) {
      setGeocode({
        status: 'error',
        message: t('adminPanel.places.edit.mapsNotReady', 'Google Maps is still loading. Please try again.'),
      });
      return;
    }
    setVerifying(true);
    setGeocode(null);
    try {
      const geocoder = new google.maps.Geocoder();
      const res = await geocoder.geocode({ address: form.address });
      if (!res.results || res.results.length === 0) {
        setGeocode({ status: 'notfound' });
      } else {
        const r = res.results[0];
        const formatted = r.formatted_address;
        const loc = r.geometry.location;
        const lat = loc.lat();
        const lng = loc.lng();
        if (editing.place_latitude != null && editing.place_longitude != null) {
          const dist = haversineM(editing.place_latitude, editing.place_longitude, lat, lng);
          setGeocode({
            status: dist > 200 ? 'mismatch' : 'match',
            formatted,
            distanceM: dist,
          });
        } else {
          setGeocode({ status: 'match', formatted, distanceM: 0 });
        }
      }
    } catch (e: any) {
      setGeocode({ status: 'error', message: e?.message ?? 'Geocoding failed' });
    } finally {
      setVerifying(false);
    }
  };

  const handleAction = async (d: AdminPlaceDispute, action: DisputeAction, label: string) => {
    if (action === 'delete_place' && !confirm(`Delete "${d.place_name}"? This cannot be undone.`)) return;
    try {
      await resolve.mutateAsync({ disputeId: d.id, action });
      toast.success(`${label} ✓`);
    } catch (e: any) {
      toast.error(e.message ?? 'Action failed');
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    if (form.categories.length === 0) {
      toast.error(t('adminPanel.places.edit.atLeastOneCategory', 'Select at least one category'));
      return;
    }
    try {
      await update.mutateAsync({
        placeId: editing.place_id,
        name: form.name || undefined,
        categories: form.categories,
        address: form.address || undefined,
        pet_policy_summary: form.pet_policy_summary || undefined,
        is_verified: form.is_verified,
      });
      toast.success('Place updated');
      setEditing(null);
    } catch (e: any) {
      toast.error(e.message ?? 'Update failed');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5" />
            {t('adminPanel.places.title', 'Pet-friendly places moderation')}
          </CardTitle>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">{t('adminPanel.places.filters.open', 'Open')}</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)
        ) : !disputes || disputes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500" />
            No reports in this view.
          </div>
        ) : (
          disputes.map((d) => {
            const cats =
              d.place_categories && d.place_categories.length > 0
                ? d.place_categories
                : d.place_category
                  ? [d.place_category]
                  : [];
            return (
            <Card key={d.id} className="border-l-4 border-l-orange-500">
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold">{d.place_name ?? '(place deleted)'}</h4>
                      {statusBadge(d.status)}
                      {cats.map((c) => (
                        <Badge key={c} variant="outline">{c}</Badge>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Reported by {d.reporter_name ?? 'anonymous'} · {format(new Date(d.created_at), 'PPp')}
                    </p>
                    {d.place_address && <p className="text-sm mt-1 flex items-center gap-1"><MapPin className="h-3 w-3" />{d.place_address}</p>}
                  </div>
                  {d.place_latitude && d.place_longitude && (
                    <Button asChild variant="outline" size="sm">
                      <a href={`https://www.google.com/maps/search/?api=1&query=${d.place_latitude},${d.place_longitude}`} target="_blank" rel="noreferrer">
                        <ExternalLink className="h-3 w-3 mr-1" /> Map
                      </a>
                    </Button>
                  )}
                </div>

                <div className="bg-muted rounded p-2 text-sm">
                  <strong>Reason: </strong>{d.reason}
                </div>

                {(d.status === 'open' || d.status === 'pending') && d.place_id && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(d)}>
                      <Pencil className="h-3 w-3 mr-1" /> Edit place
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleAction(d, 'delete_place', 'Place deleted')}>
                      <Trash2 className="h-3 w-3 mr-1" /> Delete (duplicate / closed)
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleAction(d, 'remove_owner', 'Owner removed')}>
                      <UserX className="h-3 w-3 mr-1" /> Remove owner / unverify
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleAction(d, 'mark_resolved', 'Marked resolved')}>
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Mark resolved
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleAction(d, 'reject', 'Report rejected')}>
                      <XCircle className="h-3 w-3 mr-1" /> Reject report
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
          })
        )}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit place</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>

            <div>
              <Label>{t('adminPanel.places.edit.categories', 'Categories')}</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {PLACE_CATEGORIES.map((c) => {
                  const active = form.categories.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCategory(c)}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                        active
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border hover:bg-muted'
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
              {form.categories.length === 0 && (
                <p className="text-[11px] text-destructive mt-1">
                  {t('adminPanel.places.edit.atLeastOneCategory', 'Select at least one category')}
                </p>
              )}
            </div>

            <div>
              <Label>Address</Label>
              <div className="flex gap-2">
                <Input
                  value={form.address}
                  onChange={(e) => {
                    setForm({ ...form, address: e.target.value });
                    setGeocode(null);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleVerifyAddress}
                  disabled={verifying || !form.address.trim()}
                >
                  {verifying ? <Loader2 className="h-3 w-3 animate-spin" /> : t('adminPanel.places.edit.verifyAddress', 'Verify')}
                </Button>
              </div>
              {geocode && (
                <div className="mt-2 text-xs rounded-md border p-2 space-y-1">
                  {geocode.status === 'match' && (
                    <div className="flex items-start gap-1 text-green-600">
                      <CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0" />
                      <div>
                        <div>
                          {t('adminPanel.places.edit.addressMatch', 'Address matches location ({{distance}} m)', { distance: geocode.distanceM })}
                        </div>
                        <div className="text-muted-foreground mt-0.5">{geocode.formatted}</div>
                        {geocode.formatted !== form.address && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 mt-1"
                            onClick={() => setForm((f) => ({ ...f, address: geocode.formatted }))}
                          >
                            {t('adminPanel.places.edit.useFormatted', "Use Google's formatted address")}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                  {geocode.status === 'mismatch' && (
                    <div className="flex items-start gap-1 text-amber-600">
                      <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                      <div>
                        <div>
                          {t('adminPanel.places.edit.addressMismatch', 'Address is {{distance}} m away from the place location', { distance: geocode.distanceM })}
                        </div>
                        <div className="text-muted-foreground mt-0.5">{geocode.formatted}</div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 mt-1"
                          onClick={() => setForm((f) => ({ ...f, address: geocode.formatted }))}
                        >
                          {t('adminPanel.places.edit.useFormatted', "Use Google's formatted address")}
                        </Button>
                      </div>
                    </div>
                  )}
                  {geocode.status === 'notfound' && (
                    <div className="flex items-center gap-1 text-destructive">
                      <XCircle className="h-3 w-3" />
                      {t('adminPanel.places.edit.addressNotFound', 'Google could not find this address')}
                    </div>
                  )}
                  {geocode.status === 'error' && (
                    <div className="flex items-center gap-1 text-destructive">
                      <XCircle className="h-3 w-3" />
                      {geocode.message}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label>Pet policy summary</Label>
              <Textarea value={form.pet_policy_summary} onChange={(e) => setForm({ ...form, pet_policy_summary: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_verified} onChange={(e) => setForm({ ...form, is_verified: e.target.checked })} />
              Mark as verified
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={update.isPending || form.categories.length === 0}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminPlacesPanel;
